// src/modals/settingsModals.jsx
// Modals related to settings (users, locations, sublocations)
import React from 'react';
import { USER_ROLES } from '../kegops-utils';

// User Modal - Add/Edit User
export const UserModal = ({ show, onClose, editingUser, userForm, setUserForm, onSave }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">{editingUser ? 'Edit User' : 'Add User'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block mb-2 font-medium">Name*</label>
            <input 
              type="text" 
              value={userForm.name}
              onChange={(e) => setUserForm({...userForm, name: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium">Email*</label>
            <input 
              type="email" 
              value={userForm.email}
              onChange={(e) => setUserForm({...userForm, email: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium">Role</label>
            <select 
              value={userForm.role}
              onChange={(e) => setUserForm({...userForm, role: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              {USER_ROLES.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Admin: Full access • Editor: Can edit kegs • Viewer: Read-only
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button 
            onClick={onSave}
            disabled={!userForm.name || !userForm.email}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            {editingUser ? 'Save Changes' : 'Add User'}
          </button>
          <button 
            onClick={onClose}
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Location Modal - Add/Edit Location
export const LocationModal = ({ show, onClose, editingLocation, newLocation, setNewLocation, onAdd, selectedBreweryId }) => {
  const handleTapCountChange = (e) => {
    const count = Math.max(1, Math.min(16, parseInt(e.target.value) || 0));
    setNewLocation({
      ...newLocation,
      tapCount: count,
      tapNames: Array(count).fill('').map((_, i) => newLocation.tapNames[i] || `${i+1}`)
    });
  };

  const handleTapNameChange = (idx, value) => {
    const tapNames = [...newLocation.tapNames];
    tapNames[idx] = value;
    setNewLocation({ ...newLocation, tapNames });
  };

  const [lineStatus, setLineStatus] = React.useState(null);
  const [history, setHistory] = React.useState([]);

  React.useEffect(() => {
    const load = async () => {
      if (!selectedBreweryId || !newLocation?.name || !newLocation.hasTaps) return;
      const t = await (await import('../supabaseService')).fetchTapLineStatuses(selectedBreweryId);
      if (t.success) {

        const found = t.data.find(r => r.location === newLocation.name && (r.sublocation === null || r.sublocation === ''));
        if (found) setLineStatus(found);
      }
      const lm = await (await import('../supabaseService')).fetchLocationMaintenanceRecords(selectedBreweryId, 50);
      if (lm.success) setHistory(lm.data.filter(r => r.location === newLocation.name && (r.sublocation === null || r.sublocation === '')) || []);
    };
    load();
  }, [selectedBreweryId, newLocation]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">{editingLocation ? 'Edit Location' : 'Add Location'}</h3>
        <div className="space-y-4"> 
          <input 
            type="text" 
            placeholder="Name" 
            value={newLocation.name} 
            onChange={(e) => setNewLocation({...newLocation, name: e.target.value})} 
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
          />
          <input 
            type="text" 
            placeholder="Description" 
            value={newLocation.description} 
            onChange={(e) => setNewLocation({...newLocation, description: e.target.value})} 
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
          />
          <div className="flex items-center gap-2">
            <label className="font-medium">Has Taps?</label>
            <input
              type="checkbox"
              checked={!!newLocation.hasTaps}
              onChange={e => setNewLocation({ ...newLocation, hasTaps: e.target.checked })}
            />
          </div>
          {newLocation.hasTaps && (
            <div className="space-y-2">
              <label className="font-medium">Number of Taps (1-16)</label>
              <input
                type="number"
                min={1}
                max={16}
                value={newLocation.tapCount || 1}
                onChange={handleTapCountChange}
                className="w-24 border rounded px-2 py-1"
              />
              <div className="space-y-1 mt-2">
                {Array.from({ length: newLocation.tapCount || 1 }).map((_, idx) => (
                  <input
                    key={idx}
                    type="text"
                    placeholder={`Tap ${idx+1} Name`}
                    value={newLocation.tapNames[idx] || `${idx+1}`}
                    onChange={e => handleTapNameChange(idx, e.target.value)}
                    maxLength={24}
                    className="w-full border rounded px-2 py-1 mb-1"
                  />
                ))}
              </div>

              {/* Line Cleaning Summary */}
              <div className="mt-4 p-3 border rounded bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Line Cleaning Status</div>
                  <div className="text-xs text-gray-500">Tap scan</div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  <div>Batch count: <span className="font-semibold">{lineStatus?.batch_count ?? 0}</span></div>
                  <div>Last cleaned: <span className="font-semibold">{lineStatus?.last_cleaned_at ? new Date(lineStatus.last_cleaned_at).toLocaleDateString() : 'Never'}</span></div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button onClick={async () => {
                    if (!selectedBreweryId) return alert('Select brewery first');
                    const result = await (await import('../supabaseService')).recordLocationMaintenance(selectedBreweryId, newLocation.name, null, ['Line Cleaning'], 'Recorded via Location settings');
                    if (result.success) {
                      alert('Recorded line cleaning');
                      const t = await (await import('../supabaseService')).fetchTapLineStatuses(selectedBreweryId);
                      if (t.success) setLineStatus(t.data.find(r => r.location === newLocation.name && (r.sublocation === null || r.sublocation === '')) || null);
                      const lm = await (await import('../supabaseService')).fetchLocationMaintenanceRecords(selectedBreweryId, 50);
                      if (lm.success) setHistory(lm.data.filter(r => r.location === newLocation.name && (r.sublocation === null || r.sublocation === '')) || []);
                    } else {
                      alert('Error recording: ' + result.error);
                    }
                  }} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Record Clean</button>

                  <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm">View History</button>
                </div>

                {history && history.length > 0 && (
                  <div className="mt-3 text-sm space-y-2">
                    {history.map(h => (
                      <div key={h.id} className="p-2 border rounded bg-white dark:bg-gray-800">
                        <div className="text-xs text-gray-600">{new Date(h.maintenance_date).toLocaleString()}</div>
                        <div className="text-sm font-medium">{(h.items || []).join(', ')}</div>
                        <div className="text-xs text-gray-500">Batch snapshot: {h.batch_count_snapshot ?? 0}</div>
                        {h.notes && <div className="text-xs text-gray-500 mt-1">{h.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-6">
          <button 
            onClick={onAdd} 
            disabled={
              !newLocation.name ||
              (newLocation.hasTaps && (
                !newLocation.tapCount ||
                newLocation.tapCount < 1 ||
                newLocation.tapCount > 16 ||
                newLocation.tapNames.length !== newLocation.tapCount ||
                newLocation.tapNames.some(n => !n || n.length > 24)
              ))
            }
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            {editingLocation ? 'Save' : 'Add'}
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Sublocation Modal - Add/Edit Sublocation
export const SublocationModal = ({ 
  show, onClose, editingSublocation, newSublocation, setNewSublocation, locations, onAdd, selectedBreweryId 
}) => {
  const handleTapCountChange = (e) => {
    const count = Math.max(1, Math.min(16, parseInt(e.target.value) || 0));
    setNewSublocation({
      ...newSublocation,
      tapCount: count,
      tapNames: Array(count).fill('').map((_, i) => (newSublocation.tapNames && newSublocation.tapNames[i]) || `${i+1}`)
    });
  };

  const handleTapNameChange = (idx, value) => {
    const tapNames = Array.isArray(newSublocation.tapNames) ? [...newSublocation.tapNames] : [];
    tapNames[idx] = value;
    setNewSublocation({ ...newSublocation, tapNames });
  };

  const handleToggleTaps = (checked) => {
    setNewSublocation({
      ...newSublocation,
      hasTaps: checked,
      tapCount: checked ? (newSublocation.tapCount || 1) : 0,
      tapNames: checked ? (Array(newSublocation.tapCount || 1).fill('').map((_, i) => (newSublocation.tapNames && newSublocation.tapNames[i]) || `${i+1}`)) : []
    });
  };

  const [lineStatus, setLineStatus] = React.useState(null);
  const [history, setHistory] = React.useState([]);

  React.useEffect(() => {
    const load = async () => {
      if (!selectedBreweryId || !newSublocation?.name || !newSublocation.hasTaps) return;
      const t = await (await import('../supabaseService')).fetchTapLineStatuses(selectedBreweryId);
      if (t.success) {
        const found = t.data.find(r => r.location === (locations.find(l => l.id === newSublocation.parentId)?.name) && r.sublocation === newSublocation.name);
        if (found) setLineStatus(found);
      }
      const lm = await (await import('../supabaseService')).fetchLocationMaintenanceRecords(selectedBreweryId, 50);
      if (lm.success) setHistory(lm.data.filter(r => r.location === (locations.find(l => l.id === newSublocation.parentId)?.name) && r.sublocation === newSublocation.name) || []);
    };
    load();
  }, [selectedBreweryId, newSublocation, locations]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">{editingSublocation ? 'Edit Sublocation' : 'Add Sublocation'}</h3>
        <div className="space-y-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Parent Location</label>
            {editingSublocation ? (
              <input 
                type="text" 
                value={locations.find(l => l.id === newSublocation.parentId)?.name || ''} 
                disabled 
                className="w-full border rounded px-3 py-2 bg-gray-100" 
              />
            ) : (
              <select
                value={newSublocation.parentId || ''}
                onChange={(e) => setNewSublocation({...newSublocation, parentId: e.target.value})}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Select a location...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            )}
          </div>
          <input 
            type="text" 
            placeholder="Sublocation Name" 
            value={newSublocation.name} 
            onChange={(e) => setNewSublocation({...newSublocation, name: e.target.value})} 
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
          />
          <input 
            type="text" 
            placeholder="Description" 
            value={newSublocation.description} 
            onChange={(e) => setNewSublocation({...newSublocation, description: e.target.value})} 
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
          />
          <div className="flex items-center gap-2">
            <label className="font-medium">Has Taps?</label>
            <button
              type="button"
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${newSublocation.hasTaps ? 'bg-amber-600' : 'bg-gray-300'}`}
              onClick={() => handleToggleTaps(!newSublocation.hasTaps)}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${newSublocation.hasTaps ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
          {newSublocation.hasTaps && (
            <div className="space-y-2">
              <label className="font-medium">Number of Taps (1-16)</label>
              <input
                type="number"
                min={1}
                max={16}
                value={newSublocation.tapCount || 1}
                onChange={handleTapCountChange}
                className="w-24 border rounded px-2 py-1"
              />
              <div className="space-y-1 mt-2">
                {Array.from({ length: newSublocation.tapCount || 1 }).map((_, idx) => (
                  <input
                    key={idx}
                    type="text"
                    placeholder={`Tap ${idx+1} Name`}
                    value={newSublocation.tapNames[idx] || `${idx+1}`}
                    onChange={e => handleTapNameChange(idx, e.target.value)}
                    maxLength={24}
                    className="w-full border rounded px-2 py-1 mb-1"
                  />
                ))}
              </div>

              {/* Line Cleaning Summary for Sublocation */}
              <div className="mt-4 p-3 border rounded bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Line Cleaning Status</div>
                  <div className="text-xs text-gray-500">Tap scan</div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  <div>Batch count: <span className="font-semibold">{lineStatus?.batch_count ?? 0}</span></div>
                  <div>Last cleaned: <span className="font-semibold">{lineStatus?.last_cleaned_at ? new Date(lineStatus.last_cleaned_at).toLocaleDateString() : 'Never'}</span></div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button onClick={async () => {
                    const parentName = locations.find(l => l.id === newSublocation.parentId)?.name;
                    if (!selectedBreweryId || !parentName) return alert('Select brewery and parent location first');
                    const result = await (await import('../supabaseService')).recordLocationMaintenance(selectedBreweryId, parentName, newSublocation.name, ['Line Cleaning'], 'Recorded via Sublocation settings');
                    if (result.success) {
                      alert('Recorded line cleaning');
                      const t = await (await import('../supabaseService')).fetchTapLineStatuses(selectedBreweryId);
                      if (t.success) setLineStatus(t.data.find(r => r.location === parentName && r.sublocation === newSublocation.name) || null);
                      const lm = await (await import('../supabaseService')).fetchLocationMaintenanceRecords(selectedBreweryId, 50);
                      if (lm.success) setHistory(lm.data.filter(r => r.location === parentName && r.sublocation === newSublocation.name) || []);
                    } else {
                      alert('Error recording: ' + result.error);
                    }
                  }} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Record Clean</button>

                  <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm">View History</button>
                </div>

                {history && history.length > 0 && (
                  <div className="mt-3 text-sm space-y-2">
                    {history.map(h => (
                      <div key={h.id} className="p-2 border rounded bg-white dark:bg-gray-800">
                        <div className="text-xs text-gray-600">{new Date(h.maintenance_date).toLocaleString()}</div>
                        <div className="text-sm font-medium">{(h.items || []).join(', ')}</div>
                        <div className="text-xs text-gray-500">Batch snapshot: {h.batch_count_snapshot ?? 0}</div>
                        {h.notes && <div className="text-xs text-gray-500 mt-1">{h.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-6">
          <button 
            onClick={onAdd} 
            disabled={
              !newSublocation.name ||
              !newSublocation.parentId ||
              (newSublocation.hasTaps && (
                !newSublocation.tapCount ||
                newSublocation.tapCount < 1 ||
                newSublocation.tapCount > 16 ||
                newSublocation.tapNames.length !== newSublocation.tapCount ||
                newSublocation.tapNames.some(n => !n || n.length > 24)
              ))
            }
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            {editingSublocation ? 'Save' : 'Add'}
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};